import { useState, useMemo } from "react"
import { addDays, addWeeks, format, getWeekYear, startOfWeek, subWeeks } from "date-fns"
import { fr } from "date-fns/locale"
import { useTimeMachine } from "@/contexts/TimeMachineContext"
import { useCurrentSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { computeAttendanceForPeriod } from "@/controller/attendanceCompute"
import { AttendanceStatus } from "@/types/attendanceStatus"
import { QueryRender } from "@/components/helpers/QueryRender"
import { useQuery } from "@tanstack/react-query"
import { useGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import { getDayQuarters } from "@/entities/globalSettings/globalSettings"
import { icon_outline_check } from "@/utils/svgIcons"

interface Props {
   youngId: string
}

export function IndividualAttendanceMiniCalendar({ youngId }: Props) {
   const { now } = useTimeMachine()
   const [currentDate, setCurrentDate] = useState(now)
   const { data: currentSchoolYear } = useCurrentSchoolYear()
   const { data: globalSettings } = useGlobalSettings()

   const week = useMemo(
      () =>
         Array.from({ length: 5 }, (_, i) =>
            addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), i),
         ),
      [currentDate],
   )

   // Get unique months in the week
   const months = useMemo(
      () => [...new Set(week.map((day) => format(day, "MMMM yyyy", { locale: fr })))],
      [week],
   )

   const dayQuarters = useMemo(
      () => week.map((day) => getDayQuarters(globalSettings!, day)),
      [week, globalSettings],
   )

   const getQuarterColor = (status: AttendanceStatus) => {
      switch (status) {
         case "present":
            return "bg-success"
         case "absent":
            return "bg-error"
         case "late":
            return "bg-warning"
         default:
            return "bg-base-300"
      }
   }

   const { data: quarterStatuses = {}, ...query } = useQuery({
      queryKey: [
         "collection",
         "attendance_events",
         "statistics",
         youngId,
         currentSchoolYear?.id,
         getWeekYear(currentDate),
      ],
      queryFn: async () => {
         if (!currentSchoolYear?.id) return {}

         const newStatuses: Record<string, { status: AttendanceStatus; justified: boolean }> = {}

         for (const day of week) {
            const quarters = getDayQuarters(globalSettings!, day)
            for (let i = 0; i < quarters.length; i++) {
               const quarter = quarters[i]
               const key = `${day.toISOString()}-${i}`
               const status = await computeAttendanceForPeriod({
                  young_id: youngId,
                  schoolyear_id: currentSchoolYear.id,
                  from: quarter.start,
                  to: quarter.end,
               })
               newStatuses[key] = status
            }
         }

         return newStatuses
      },
      enabled: !!currentSchoolYear?.id && !!globalSettings,
   })

   return (
      <QueryRender query={query} data={quarterStatuses}>
         {() => (
            <div className="flex flex-col gap-4">
               <div className="flex items-center justify-between">
                  <button
                     className="btn btn-ghost"
                     onClick={() => setCurrentDate((d) => subWeeks(d, 1))}
                  >
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M15 19l-7-7 7-7"
                        />
                     </svg>
                  </button>
                  <div className="font-medium">{months.join(" - ")}</div>
                  <button
                     className="btn btn-ghost"
                     onClick={() => setCurrentDate((d) => addWeeks(d, 1))}
                  >
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M9 5l7 7-7 7"
                        />
                     </svg>
                  </button>
               </div>
               <div className="flex gap-4 justify-around">
                  {week.map((day, idx) => {
                     const quarters = dayQuarters[idx]
                     return (
                        <div key={day.toISOString()} className="flex flex-col items-center">
                           <div className="font-medium mb-2">
                              {format(day, "ccc d", { locale: fr })}
                           </div>
                           <div className="flex flex-col gap-2 items-center mx-auto">
                              {quarters.map((quarter, index) => {
                                 const key = `${day.toISOString()}-${index}`
                                 const status = quarterStatuses[key] || {
                                    status: "present",
                                    justified: true,
                                 }
                                 return (
                                    <div
                                       key={index}
                                       className={`w-6 h-8 text-white flex items-center justify-center rounded-lg ${getQuarterColor(status.status)}`}
                                       title={`${format(quarter.start, "HH:mm")} - ${format(quarter.end, "HH:mm")}`}
                                    >
                                       {["absent", "late"].includes(status.status) &&
                                          status.justified && (  
                                             icon_outline_check
                                          )}
                                    </div>
                                 )
                              })}
                              <svg
                                 xmlns="http://www.w3.org/2000/svg"
                                 className="h-4 w-4"
                                 fill="none"
                                 viewBox="0 0 24 24"
                                 stroke="currentColor"
                              >
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                 />
                              </svg>
                           </div>
                        </div>
                     )
                  })}
               </div>
            </div>
         )}
      </QueryRender>
   )
}
